import { z } from 'zod'
import { parseYuanToCents } from './format'
import { blindStructureSchema, emptyBlindStructure } from './blind-structure'
import {
  awardThresholdKinds,
  awardTypes,
  bountyCountSchema,
  hunterEliminationModes,
  hunterRewardSchema,
  maxChipAmount,
  promotionOfferSchema,
  type AwardTierSummary,
  type GameConfig,
  type HunterGameConfig,
  type PromotionOffer,
} from './game-template'
import { playerPaymentMethods } from './enum-groups'
import type { GameClockView } from './game-clock'
import type { PulledPlayerView } from './game-pull'
import type { PlayerProfile } from './player-profile'
import type { MemberPointRecordView } from './member-points'
import type { MotivationPrize } from './motivation'
import type { StaffRole } from './staff-form'
import {
  douyinChannelFieldsSchema,
  meituanChannelFieldsSchema,
  type ChannelKind,
} from './channel'
import { tenantProfiles } from './branding'
import { tenantCodeSchema } from './tenant-code'
import type { TableGoodsOrderView } from './products'
import { importFileSchema } from './import-batch'
import type { MemberImportSkip } from './member-import'
import { listingToggleStatuses, type ListingStatus } from './listing-status'
import { isShowroomGame } from './showroom'
import {
  voucherScopeKey,
  voucherScopeSchema,
  voucherValidityFromForm,
  voucherValidityKinds,
  voucherValidityUnits,
} from './voucher'

export * from './auto-message'
export * from './signup-flow'
export * from './channel'
export * from './blind-structure'
export * from './boss'
export * from './chips'
export * from './client-event'
export * from './entry-rules'
export * from './entry-view'
export * from './branding'
export * from './game-clock'
export * from './level-speech'
export * from './game-finish'
export * from './game-pull'
export * from './game-template'
export * from './gateway'
export * from './commerce'
export * from './content'
export * from './display'
export * from './display-clock-extras'
export * from './display-leaderboard'
export * from './display-layout'
export * from './display-stick'
export * from './printer'
export * from './endpoints'
export * from './enum-groups'
export * from './format'
export * from './labels'
export * from './lakala'
export * from './list'
export * from './manual-game'
export * from './manual-game-import'
export * from './manual-leaderboard'
export * from './manual-leaderboard-import'
export * from './import-batch'
export * from './member-number'
export * from './member-import'
export * from './member-points'
export * from './member-voucher-record'
export * from './member-leaderboard'
export * from './season'
export * from './leaderboard-poster'
export * from './leaderboard-stats'
export * from './leaderboard-award'
export * from './listing-status'
export * from './featured-board'
export * from './member-rankings'
export * from './motivation'
export * from './settlement'
export * from './signup-quote'
export * from './signup-proxy'
export * from './site-lead'
export * from './player-profile'
export * from './products'
export * from './purchase-limit'
export * from './rankings'
export * from './ranking-share'
export * from './seat-token'
export * from './seat-qr'
export * from './showroom'
export * from './storage-keys'
export * from './subscribe-message'
export * from './subscription'
export { staffRoles, staffSchema, staffUpdateSchema } from './staff-form'
export * from './tenant-access'
export * from './tenant-code'
export * from './tree'
export * from './voucher'
export * from './wecom'

export const seatKinds = ['player', 'dealer'] as const
/** 每张物理桌固定的顾客位数量，裁判位（seatNo 0）另算 */
export const playerSeatCount = 10
export const gameKinds = ['tournament', 'normal'] as const
export const gameStatuses = ['preparing', 'running', 'finished'] as const
export const gameTableStatuses = ['running', 'closed'] as const
export const gamePlayerStatuses = ['seated', 'waiting', 'eliminated'] as const
export const gameRequestKinds = ['entry', 'rebuy'] as const
export const gameRequestStatuses = [
  'pending',
  'confirmed',
  'cancelled',
] as const
export const requestAlertModes = ['immediate', 'timeout'] as const
export type RequestAlertMode = (typeof requestAlertModes)[number]
export const requestAlertMinTimeoutSeconds = 10
export const requestAlertDefaultTimeoutSeconds = 60
export const gameEventKinds = [
  'player_seated',
  'player_chips_added',
  'player_chips_removed',
  'player_seat_changed',
  'player_eliminated',
  'player_transferred_out',
  'player_transferred_in',
  'dealer_changed',
  'motivation_granted',
  'motivation_revoked',
] as const
export const entryKinds = ['entry', 'rebuy'] as const
export const productKinds = ['ticket', 'goods'] as const
export const tenantPlans = ['trial', 'standard', 'pro', 'display'] as const
export const tenantStatuses = ['active', 'suspended', 'expired'] as const
export const merchantStatuses = [
  'unconfigured',
  'pending',
  'active',
  'rejected',
] as const
export const orderStatuses = [
  'unpaid',
  'paid',
  'refund_requested',
  'refunding',
  'cancelled',
  'refunded',
] as const
export const refundStatuses = [
  'requested',
  'pending',
  'processing',
  'succeeded',
  'abnormal',
  'failed',
] as const
export const paymentMethods = ['wechat', 'alipay', 'cash', 'lakala'] as const
export const orderItemKinds = [
  'entry',
  'rebuy',
  'beverage',
  'food',
  'service',
] as const
export const paymentStatuses = [
  'created',
  'paid',
  'closed',
  'failed',
  'refunded',
] as const
export const orderCancelReasons = ['member', 'timeout'] as const
export const genders = ['male', 'female', 'unknown'] as const
export const memberLevels = ['normal', 'vip'] as const
export const memberRoles = ['customer', 'staff', 'agent'] as const
export const memberStatuses = ['active', 'disabled'] as const
export const voucherSources = [
  'motivation',
  'award',
  'manual',
  'point_exchange',
  'refund',
  'channel',
  'hunter',
  'new_customer',
  'purchase',
] as const
export const orderAdjustmentKinds = [
  'ticket',
  'comp',
  'promotion',
  'new_customer',
] as const
export const orderAdjustmentStatuses = [
  'reserved',
  'applied',
  'voided',
] as const

export type { StaffRole } from './staff-form'
export type SeatKind = (typeof seatKinds)[number]
export type GameKind = (typeof gameKinds)[number]
export type GameStatus = (typeof gameStatuses)[number]
export type GameTableStatus = (typeof gameTableStatuses)[number]
export type GamePlayerStatus = (typeof gamePlayerStatuses)[number]
export type GameRequestKind = (typeof gameRequestKinds)[number]
export type GameRequestStatus = (typeof gameRequestStatuses)[number]
export type GameEventKind = (typeof gameEventKinds)[number]
export type EntryKind = (typeof entryKinds)[number]
export type TenantPlan = (typeof tenantPlans)[number]
export type TenantStatus = (typeof tenantStatuses)[number]
export type MerchantStatus = (typeof merchantStatuses)[number]
export type OrderStatus = (typeof orderStatuses)[number]
export type OrderCancelReason = (typeof orderCancelReasons)[number]
export type RefundStatus = (typeof refundStatuses)[number]
export type PaymentMethod = (typeof paymentMethods)[number]
export type OrderItemKind = (typeof orderItemKinds)[number]
export type PaymentStatus = (typeof paymentStatuses)[number]
export type Gender = (typeof genders)[number]
export type MemberLevel = (typeof memberLevels)[number]
export type MemberRole = (typeof memberRoles)[number]
export type MemberStatus = (typeof memberStatuses)[number]
export type VoucherSource = (typeof voucherSources)[number]
export type ProductKind = (typeof productKinds)[number]
export type OrderAdjustmentKind = (typeof orderAdjustmentKinds)[number]
export type OrderAdjustmentStatus = (typeof orderAdjustmentStatuses)[number]

export interface StaffListItem {
  id: string
  name: string
  employeeNo: string
  phone: string | null
  role: StaffRole
  roleLabel: string
  salary: number
  salaryLabel: string
  storeId: string | null
  storeName: string
  wechatOpenid: string | null
  wechatBoundLabel: string
  status: ListingStatus
  statusLabel: string
}

export type OrderDeliveryStatus = GameRequestStatus | 'none'

export interface OrderListItem {
  id: string
  orderNo: string
  storeId: string
  storeName: string
  memberId: string | null
  memberNo: string | null
  nickname: string
  avatarUrl: string | null
  tableName: string
  seatNo: number | null
  gameName: string | null
  itemLabel: string
  paymentLabel: string
  status: OrderStatus
  statusLabel: string
  deliveryStatus: OrderDeliveryStatus
  deliveryLabel: string
  amount: number
  amountLabel: string
  createdAt: string
  createdAtLabel: string
  updatedAt: string
  updatedAtLabel: string
  refundId: string | null
  refundStatus: RefundStatus | null
  refundStatusLabel: string
  refundAmountLabel: string
  refundReason: string | null
  refundActorName: string | null
  isDelivered: boolean
  isGameOrder: boolean
  hasMember: boolean
  canCancel: boolean
  canRefund: boolean
  canReject: boolean
  canSync: boolean
}

export interface PlayerOrderPromotionView {
  kind: OrderAdjustmentKind
  name: string
  quantity: number
  amount: number
  amountLabel: string
}

export interface PlayerOrderItemView {
  name: string
  quantity: number
  amount: number
  amountLabel: string
  promotions: PlayerOrderPromotionView[]
}

export interface PlayerOrderListItem {
  id: string
  orderNo: string
  storeName: string
  tableName: string
  seatNo: number | null
  locationLabel: string
  status: OrderStatus
  statusLabel: string
  items: PlayerOrderItemView[]
  createdAt: string
  createdAtLabel: string
  amount: number
  amountLabel: string
  canCancel: boolean
  canRefund: boolean
  /** 当前会员为对方代付时，对方的昵称 */
  beneficiaryNickname: string | null
  /** 当前会员被代付时，付款人的昵称 */
  payerNickname: string | null
}

export interface PlayerOrderListResult {
  orders: PlayerOrderListItem[]
}

function yuanAmountSchema(label: string) {
  return z.preprocess(
    (value) => {
      if (typeof value !== 'string') return value
      try {
        return parseYuanToCents(value)
      } catch {
        return Number.NaN
      }
    },
    z
      .number({ error: `${label}格式不合法` })
      .int(`${label}必须精确到分`)
      .min(0, `${label}不能为负数`),
  )
}

export const loginSchema = z.object({
  employeeNo: z.string().trim().min(1, '请输入工号'),
  password: z.string().min(6, '密码至少 6 位'),
  tenantId: z.guid('租户参数不合法').optional(),
})

export const switchIdentitySchema = z.object({
  staffId: z.guid('员工参数不合法'),
})

export const reservedIdPrefix = '00000000'
export const sandboxTenantId = '00000000-0000-4000-8000-000000000001'
export const sandboxDemoStaffId = '00000000-0000-4000-8000-000000000010'

export function isReservedId(id: string): boolean {
  return id.startsWith(reservedIdPrefix)
}

export function isSandboxTenant(id: string): boolean {
  return id === sandboxTenantId
}

export function shouldShowDemoNotice(input: {
  tenantId?: string | null
  gameId?: string | null
}): boolean {
  return Boolean(
    (input.tenantId && isSandboxTenant(input.tenantId)) ||
    (input.gameId && isShowroomGame(input.gameId)),
  )
}

/** 测试租户且没有微信侧单据：样板间空壳单，不出网 */
export function isVirtualWechatPayment(input: {
  tenantId: string
  method: string
  hasWechatPayment: boolean
}) {
  return (
    isSandboxTenant(input.tenantId) &&
    input.method === 'wechat' &&
    !input.hasWechatPayment
  )
}

export const createSandboxVillainsSchema = z.object({
  quantity: z.coerce
    .number()
    .int('假人数量必须是整数')
    .min(1, '至少生成 1 个假人')
    .max(99, '单次最多 99 个假人'),
})

export const createSandboxGameSchema = z.object({
  staffId: z.guid('请选择裁判'),
  tableId: z.guid('请选择桌台'),
  gameTemplateId: z.guid('请选择赛制'),
  villainCount: z.coerce
    .number()
    .int('假人数量必须是整数')
    .min(0, '假人数量不能为负数')
    .max(playerSeatCount, '假人不能超过座位数'),
})

export const storeSchema = z.object({
  name: z.string().trim().min(2, '门店名称至少 2 个字'),
  address: z.string().trim().optional().default(''),
  meituan: meituanChannelFieldsSchema.nullable().optional(),
  douyin: douyinChannelFieldsSchema.nullable().optional(),
})

export type StoreDraft = z.input<typeof storeSchema>

export const tenantCodeSettingsSchema = z.object({
  code: tenantCodeSchema,
})

export const tenantSchema = z.object({
  name: z.string().trim().min(2, '租户名称至少 2 个字'),
  code: tenantCodeSchema,
  profile: z.enum(tenantProfiles).default('integrated'),
  plan: z.enum(tenantPlans).default('trial'),
  status: z.enum(tenantStatuses).default('active'),
  expiresAt: z.string().datetime({ offset: true }).nullable().default(null),
})

export const tableSchema = z.object({
  storeId: z.string().uuid('请选择所属门店'),
  name: z.string().trim().min(1, '请输入桌台名称'),
})

function chipCountSchema(label: string) {
  return z.coerce
    .number({ error: `${label}必须是数字` })
    .int(`${label}必须是整数`)
    .positive(`${label}必须大于 0`)
    .max(maxChipAmount, `${label}超出可用范围`)
}

/** 女士价这类可关闭的定价项：空串与 null 都归一成 null，填了就按元转分 */
function nullableYuanAmountSchema(label: string) {
  return z.preprocess(
    (value) => {
      if (value === '' || value === undefined || value === null) return null
      if (typeof value !== 'string') return value
      try {
        return parseYuanToCents(value)
      } catch {
        return Number.NaN
      }
    },
    z
      .number({ error: `${label}格式不合法` })
      .int(`${label}必须精确到分`)
      .min(0, `${label}不能为负数`)
      .nullable(),
  )
}

export const newCustomerSettingsSchema = z.object({
  isNewCustomerEntryFree: z.boolean(),
})

export const tableQrShopSettingsSchema = z.object({
  isTableQrShopEntry: z.boolean(),
})

export const signupConfirmationSettingsSchema = z.object({
  isSignupAutoConfirmed: z.boolean(),
})

export const requestAlertSettingsSchema = z.object({
  requestAlertMode: z.enum(requestAlertModes, {
    error: '提醒方式只能填立刻提醒 / 超时提醒',
  }),
  requestAlertTimeoutSeconds: z
    .number({ error: '超时秒数必须是数字' })
    .int('超时秒数必须是整数')
    .min(
      requestAlertMinTimeoutSeconds,
      `超时秒数至少 ${requestAlertMinTimeoutSeconds} 秒`,
    ),
})

export const tenantTicketSettingsSchema = z
  .object({
    ticketValueAmount: yuanAmountSchema('比赛券面额').refine(
      (value) => value > 0,
      '比赛券面额必须大于 0',
    ),
    ticketValueAmountFemale: nullableYuanAmountSchema('女士比赛券面额'),
  })
  .superRefine((data, context) => {
    if (
      data.ticketValueAmountFemale !== null &&
      data.ticketValueAmountFemale <= 0
    )
      context.addIssue({
        code: 'custom',
        path: ['ticketValueAmountFemale'],
        message: '女士比赛券面额必须大于 0',
      })
  })

const awardPrizeSchema = z
  .object({
    awardType: z.enum(awardTypes),
    count: z.coerce
      .number({ error: '奖励数量必须是数字' })
      .int('奖励数量必须是整数')
      .positive('奖励数量必须大于 0'),
    voucherId: z.string().uuid('票券不合法').nullable().optional(),
  })
  .superRefine((prize, context) => {
    if (prize.awardType === 'voucher' && !prize.voucherId)
      context.addIssue({
        code: 'custom',
        path: ['voucherId'],
        message: '请选择奖励票券',
      })
    if (prize.awardType !== 'voucher' && prize.voucherId)
      context.addIssue({
        code: 'custom',
        path: ['voucherId'],
        message: '积分和比赛券不能绑定票券模板',
      })
  })

const awardPlaceSchema = z.object({
  prizes: z.array(awardPrizeSchema).min(1, '至少添加一项奖励'),
})

export const gameTemplateAwardSchema = z.object({
  threshold: z.coerce
    .number({ error: '阈值必须是数字' })
    .int('阈值必须是整数')
    .positive('阈值必须大于 0'),
  places: z.array(awardPlaceSchema).min(1, '至少添加一个名额'),
})

export const gameTemplateSchema = z
  .object({
    tenantId: z.string().uuid('请选择所属租户'),
    name: z.string().trim().min(2, '项目名称至少 2 个字'),
    kind: z.enum(gameKinds),
    entryPrice: yuanAmountSchema('入场价格'),
    rebuyPrice: yuanAmountSchema('续场价格'),
    femaleEntryPrice: nullableYuanAmountSchema('女士入场价格'),
    femaleRebuyPrice: nullableYuanAmountSchema('女士续场价格'),
    smallBlind: z.coerce
      .number({ error: '前位基础分必须是数字' })
      .int('前位基础分必须是整数')
      .positive('前位基础分必须大于 0')
      .default(10),
    bigBlind: z.coerce
      .number({ error: '后位基础分必须是数字' })
      .int('后位基础分必须是整数')
      .positive('后位基础分必须大于 0')
      .default(20),
    entryChips: chipCountSchema('入场计分量').default(10_000),
    rebuyChips: chipCountSchema('续场计分量').default(10_000),
    blindStructure: blindStructureSchema.default(emptyBlindStructure()),
    promotionEnabled: z.boolean().default(false),
    promotionOffer: promotionOfferSchema.nullable().default(null),
    hunterEnabled: z.boolean().default(false),
    hunterIsRequired: z.boolean().default(false),
    hunterIsOnce: z.boolean().default(false),
    hunterAllowsTwoTickets: z.boolean().default(false),
    hunterElimination: z.enum(hunterEliminationModes).default('select'),
    signupThrottled: z.boolean().default(false),
    hunterEntryPrice: nullableYuanAmountSchema('猎人入场价格'),
    hunterRebuyPrice: nullableYuanAmountSchema('猎人续场价格'),
    hunterFemaleEntryPrice: nullableYuanAmountSchema('猎人女士入场价格'),
    hunterFemaleRebuyPrice: nullableYuanAmountSchema('猎人女士续场价格'),
    hunterRewards: z.array(hunterRewardSchema).default([]),
    awardsEnabled: z.boolean().default(false),
    awardThresholdKind: z.enum(awardThresholdKinds).default('player_count'),
    awards: z.array(gameTemplateAwardSchema).default([]),
    motivationsEnabled: z.boolean().default(true),
    motivationIds: z.array(z.string().uuid('激励不合法')).default([]),
  })
  .superRefine((data, context) => {
    if (data.promotionEnabled && data.promotionOffer === null)
      context.addIssue({
        code: 'custom',
        path: ['promotionOffer'],
        message: '请选择促销选项',
      })
    if (
      data.promotionEnabled &&
      data.promotionOffer?.kind === 'price' &&
      'priceAmount' in data.promotionOffer &&
      data.promotionOffer.priceAmount >= data.entryPrice
    )
      context.addIssue({
        code: 'custom',
        path: ['promotionOffer'],
        message: '促销价必须低于入场价',
      })
    if ((data.femaleEntryPrice === null) !== (data.femaleRebuyPrice === null))
      context.addIssue({
        code: 'custom',
        path: [
          data.femaleEntryPrice === null
            ? 'femaleEntryPrice'
            : 'femaleRebuyPrice',
        ],
        message: '女士入场价与女士续场价需要同时填写',
      })
    const hunterFollowsStandard = data.hunterEnabled && data.hunterIsRequired
    if (
      !hunterFollowsStandard &&
      (data.hunterEntryPrice === null) !== (data.hunterRebuyPrice === null)
    )
      context.addIssue({
        code: 'custom',
        path: [
          data.hunterEntryPrice === null
            ? 'hunterEntryPrice'
            : 'hunterRebuyPrice',
        ],
        message: '猎人入场价与猎人续场价需要同时填写',
      })
    if (
      !hunterFollowsStandard &&
      (data.hunterFemaleEntryPrice === null) !==
        (data.hunterFemaleRebuyPrice === null)
    )
      context.addIssue({
        code: 'custom',
        path: [
          data.hunterFemaleEntryPrice === null
            ? 'hunterFemaleEntryPrice'
            : 'hunterFemaleRebuyPrice',
        ],
        message: '猎人女士入场价与猎人女士续场价需要同时填写',
      })
    if (data.hunterEnabled) {
      if (!hunterFollowsStandard && data.hunterEntryPrice === null)
        context.addIssue({
          code: 'custom',
          path: ['hunterEntryPrice'],
          message: '请填写猎人入场价格',
        })
      if (!hunterFollowsStandard && data.hunterRebuyPrice === null)
        context.addIssue({
          code: 'custom',
          path: ['hunterRebuyPrice'],
          message: '请填写猎人续场价格',
        })
      if (data.hunterRewards.length === 0)
        context.addIssue({
          code: 'custom',
          path: ['hunterRewards'],
          message: '请至少添加一项猎人奖励',
        })
      if (
        !hunterFollowsStandard &&
        data.femaleEntryPrice !== null &&
        data.hunterFemaleEntryPrice === null
      )
        context.addIssue({
          code: 'custom',
          path: ['hunterFemaleEntryPrice'],
          message: '开启女士价时请同时填写猎人女士价',
        })
    } else if (
      data.hunterIsRequired ||
      data.hunterAllowsTwoTickets ||
      data.hunterElimination === 'count'
    ) {
      context.addIssue({
        code: 'custom',
        path: ['hunterEnabled'],
        message: '请先开启猎人赛',
      })
    }
    if (data.hunterFemaleEntryPrice !== null && data.femaleEntryPrice === null)
      context.addIssue({
        code: 'custom',
        path: ['hunterFemaleEntryPrice'],
        message: '请先开启女士价',
      })
    if (data.bigBlind < data.smallBlind)
      context.addIssue({
        code: 'custom',
        path: ['bigBlind'],
        message: '后位基础分不能小于前位基础分',
      })
    const levelCount = data.blindStructure.entries.filter(
      (entry) => entry.kind === 'level',
    ).length
    if (levelCount === 0)
      context.addIssue({
        code: 'custom',
        path: ['blindStructure'],
        message: '至少需要一个计分级别',
      })
    if (data.kind === 'normal') {
      if (data.awardsEnabled || data.awards.length > 0)
        context.addIssue({
          code: 'custom',
          path: ['awardsEnabled'],
          message: '常规赛不设奖项',
        })
    } else if (data.awardsEnabled && data.awards.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['awards'],
        message: '请至少添加一个奖项',
      })
    }
    if (data.kind !== 'normal') {
      const seen = new Set<number>()
      for (const [index, award] of data.awards.entries()) {
        if (seen.has(award.threshold))
          context.addIssue({
            code: 'custom',
            path: ['awards', index, 'threshold'],
            message: '阈值不能重复',
          })
        seen.add(award.threshold)
      }
    }
  })
  .transform((data) => {
    const next = { ...data, hunterIsOnce: false }
    if (!next.hunterEnabled) {
      return {
        ...next,
        hunterIsRequired: false,
        hunterAllowsTwoTickets: false,
        hunterElimination: 'select' as const,
      }
    }
    if (!next.hunterIsRequired) return next
    return {
      ...next,
      hunterAllowsTwoTickets: false,
      hunterEntryPrice: data.entryPrice,
      hunterRebuyPrice: data.rebuyPrice,
      hunterFemaleEntryPrice: data.femaleEntryPrice,
      hunterFemaleRebuyPrice: data.femaleRebuyPrice,
    }
  })

export const voucherSchema = z
  .object({
    tenantId: z.string().uuid('请选择所属租户'),
    name: z.string().trim().min(2, '票券名称至少 2 个字'),
    valueAmount: yuanAmountSchema('金额').refine(
      (value) => value > 0,
      '票券面额必须大于 0',
    ),
    valueAmountFemale: nullableYuanAmountSchema('女士票券面额'),
    scopes: z.array(voucherScopeSchema).min(1, '请至少选择一个适用商品'),
    isMain: z.boolean().default(false),
    validityKind: z.enum(voucherValidityKinds).default('none'),
    relativeAmount: z.preprocess(
      (value) => (value === '' || value === undefined ? null : value),
      z
        .number({ error: '请填写有效期' })
        .int('有效期必须是整数')
        .min(1, '有效期至少 1')
        .max(999, '有效期最多 999')
        .nullable(),
    ),
    relativeUnit: z.enum(voucherValidityUnits).nullable().default('day'),
    absoluteExpiresAt: z.preprocess(
      (value) => (value === '' || value === undefined ? null : value),
      z.string().nullable(),
    ),
  })
  .superRefine((data, context) => {
    const keys = data.scopes.map(voucherScopeKey)
    if (new Set(keys).size !== keys.length)
      context.addIssue({
        code: 'custom',
        path: ['scopes'],
        message: '适用范围不能重复',
      })
    if (data.valueAmountFemale !== null && data.valueAmountFemale <= 0)
      context.addIssue({
        code: 'custom',
        path: ['valueAmountFemale'],
        message: '女士票券面额必须大于 0',
      })
    if (data.validityKind === 'relative') {
      if (data.relativeAmount == null)
        context.addIssue({
          code: 'custom',
          path: ['relativeAmount'],
          message: '请填写有效期',
        })
      if (data.relativeUnit == null)
        context.addIssue({
          code: 'custom',
          path: ['relativeUnit'],
          message: '请选择有效期单位',
        })
    }
    if (data.validityKind === 'absolute') {
      if (!data.absoluteExpiresAt)
        context.addIssue({
          code: 'custom',
          path: ['absoluteExpiresAt'],
          message: '请选择过期时间',
        })
      else {
        try {
          voucherValidityFromForm(data)
        } catch {
          context.addIssue({
            code: 'custom',
            path: ['absoluteExpiresAt'],
            message: '过期时间不合法',
          })
        }
      }
    }
  })

export const voucherStatusSchema = z.object({
  status: z.enum(listingToggleStatuses, { error: '状态只能填可用 / 停用' }),
  confirmMapped: z.boolean().optional(),
})

const optionalDealerStaffId = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.string().uuid('请选择当班裁判').optional(),
)

export const gameStartSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('join'),
    gameId: z.string().uuid('请选择赛事'),
    dealerStaffId: optionalDealerStaffId,
  }),
  z.object({
    mode: z.literal('create'),
    name: z.string().trim().min(1, '请填写赛事名称'),
    gameTemplateId: z.string().uuid('请选择活动项目'),
    dealerStaffId: optionalDealerStaffId,
  }),
])

const memberPhoneSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z
    .string()
    .regex(/^1\d{10}$/, '请输入正确的 11 位手机号')
    .nullable(),
)

export const memberSchema = z.object({
  nickname: z
    .string()
    .trim()
    .min(1, '请输入用户名')
    .max(24, '用户名最多 24 个字'),
  phone: memberPhoneSchema,
  tenantId: z.string().uuid('请选择归属租户'),
})

export const memberTicketQuantitySchema = z.coerce
  .number()
  .int('比赛券数量必须是整数')
  .min(0, '比赛券数量不能为负数')
  .max(999, '比赛券数量最多 999')

export const memberVoucherQuantitySchema = z.object({
  voucherId: z.string().uuid('票券不合法'),
  quantity: z.coerce
    .number()
    .int('票券数量必须是整数')
    .min(0, '票券数量不能为负数')
    .max(999, '票券数量最多 999'),
})

const memberVoucherAdjustmentsSchema = z
  .array(memberVoucherQuantitySchema)
  .superRefine((rows, context) => {
    const seen = new Set<string>()
    for (const [index, row] of rows.entries()) {
      if (!seen.has(row.voucherId)) {
        seen.add(row.voucherId)
        continue
      }
      context.addIssue({
        code: 'custom',
        path: [index, 'voucherId'],
        message: '票券不能重复',
      })
    }
  })

export const memberNoSchema = z
  .string()
  .trim()
  .min(1, '请输入会员号')
  .max(20, '会员号最多 20 个字符')

export const memberManagementSchema = memberSchema
  .omit({ tenantId: true, nickname: true })
  .partial()
  .extend({
    memberNo: memberNoSchema.optional(),
    storeId: z.preprocess(
      (value) => (value === '' ? null : value),
      z.string().uuid('请选择归属门店').nullable().optional(),
    ),
    tickets: memberTicketQuantitySchema.optional(),
    points: z.coerce
      .number()
      .int('积分必须是整数')
      .min(0, '积分不能为负数')
      .optional(),
    vouchers: memberVoucherAdjustmentsSchema.optional(),
    gender: z.enum(genders, { error: '性别只能填男 / 女 / 未知' }).optional(),
  })

export const memberMergeSchema = z.object({
  targetMemberId: z.string().uuid('请选择并入的会员'),
})

export const memberStatusUpdateSchema = z.object({
  status: z.enum(memberStatuses, { error: '状态只能填正常 / 停用' }),
})

export const listingStatusUpdateSchema = z.object({
  status: z.enum(listingToggleStatuses, { error: '状态只能填可用 / 停用' }),
})

export function parseListingStatusUpdate(payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload))
    return null
  if (Object.keys(payload).length !== 1 || !('status' in payload)) return null
  const parsed = listingStatusUpdateSchema.safeParse(payload)
  return parsed.success ? parsed.data : null
}

/** 赛事是否计入榜单与统计。停用不影响订单、营收与积分账本 */
export const gameCountedUpdateSchema = z.object({
  isCounted: z.boolean({ error: '请选择是否计入榜单' }),
  reason: z.preprocess(
    (value) => (value === '' || value === undefined ? null : value),
    z.string().max(200, '原因不能超过 200 字').nullable(),
  ),
})

export type GameCountedUpdate = z.infer<typeof gameCountedUpdateSchema>

/** 只带计入开关的 PATCH，与补录赛事的整单更新区分 */
export function parseGameCountedUpdate(payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload))
    return null
  if (!('isCounted' in payload)) return null
  const extras = Object.keys(payload).filter(
    (key) => key !== 'isCounted' && key !== 'reason',
  )
  if (extras.length > 0) return null
  const parsed = gameCountedUpdateSchema.safeParse(payload)
  return parsed.success ? parsed.data : null
}

export const memberCreateSchema = memberSchema.omit({ tenantId: true }).extend({
  tenantId: z.string().uuid('请选择归属租户').optional(),
  storeId: z.string().uuid('请选择归属门店'),
})

const blankToDefault = (fallback: string) => (value: unknown) =>
  value === '' || value === undefined ? fallback : value

export const memberImportRowSchema = z.object({
  row: z.coerce.number().int('行号不合法').min(2, '行号不合法'),
  memberNo: z.preprocess(
    (value) => (value === '' || value === undefined ? null : value),
    memberNoSchema.nullable(),
  ),
  nickname: z
    .string()
    .trim()
    .min(1, '请填写用户名')
    .max(24, '用户名最多 24 个字'),
  phone: memberPhoneSchema,
  gender: z.preprocess(
    blankToDefault('unknown'),
    z.enum(genders, { error: '性别只能填男 / 女 / 未知' }),
  ),
  level: z.preprocess(
    blankToDefault('normal'),
    z.enum(memberLevels, { error: '等级只能填普通会员 / VIP' }),
  ),
  role: z.preprocess(
    blankToDefault('customer'),
    z.enum(memberRoles, { error: '角色只能填顾客 / 员工 / 代理' }),
  ),
  status: z.preprocess(
    blankToDefault('active'),
    z.enum(memberStatuses, { error: '状态只能填正常 / 停用' }),
  ),
  birthday: z.preprocess(
    (value) => (value === '' || value === undefined ? null : value),
    z.iso.date('生日格式应为 2000-01-01').nullable(),
  ),
})

export const memberImportSchema = z
  .object({
    storeId: z.string().uuid('请选择归属门店'),
    rows: z
      .array(memberImportRowSchema)
      .min(1, '表格没有可导入的会员')
      .max(500, '单次最多导入 500 条会员'),
  })
  .and(importFileSchema)

export const playerNicknameSchema = z.object({
  nickname: z
    .string()
    .trim()
    .min(1, '请输入顾客昵称')
    .max(24, '顾客昵称最多 24 个字'),
})

export const seatPlayerSchema = playerNicknameSchema.extend({
  seatToken: z.string().min(12, '座位凭证不合法'),
  code: z.string().trim().min(1, '微信登录凭证不合法'),
  phoneCode: z.string().trim().min(1, '手机号授权凭证不合法').optional(),
})

export const signupVoucherSelectionSchema = z.object({
  voucherId: z.string().uuid('票券不合法'),
  count: z
    .number({ error: '用券数量不合法' })
    .int('用券数量必须是整数')
    .min(1, '用券数量至少 1 张')
    .max(20, '单次最多使用 20 张'),
})

export const entrySchema = z
  .object({
    seatToken: z.string().min(12),
    count: z
      .number({ error: '报名次数不合法' })
      .int('报名次数必须是整数')
      .min(1, '报名次数至少 1 次')
      .max(20, '单次最多报名 20 次'),
    ticketCount: z
      .number({ error: '用券数量不合法' })
      .int('用券数量必须是整数')
      .min(0, '用券数量不能为负')
      .default(0),
    vouchers: z.array(signupVoucherSelectionSchema).default([]),
    isHunter: z.boolean().optional(),
    beneficiaryMemberId: z.string().uuid('代报名会员不合法').optional(),
  })
  .superRefine((data, ctx) => {
    if (data.beneficiaryMemberId) {
      if (data.ticketCount > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['ticketCount'],
          message: '代报名不能使用票券',
        })
      }
      if (data.vouchers.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['vouchers'],
          message: '代报名不能使用票券',
        })
      }
    }
    // 猎人行可以多一张比赛券，精确上限在报价里按当场开关计算
    if (data.ticketCount > data.count + 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['ticketCount'],
        message: '用券数量不能超过可抵次数',
      })
    }
    const voucherIds = new Set<string>()
    let voucherCount = 0
    for (const [index, row] of data.vouchers.entries()) {
      if (voucherIds.has(row.voucherId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['vouchers', index, 'voucherId'],
          message: '同一票券不能重复提交',
        })
      }
      voucherIds.add(row.voucherId)
      voucherCount += row.count
    }
    if (data.ticketCount + voucherCount > data.count + 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['vouchers'],
        message: '用券数量不能超过可抵次数',
      })
    }
  })

export const entryStatusQuerySchema = z.object({
  seatToken: z.string().min(12),
})

export const signupBeneficiariesQuerySchema = entryStatusQuerySchema

export const cancelOrderSchema = z.object({
  orderId: z.string().uuid(),
  seatToken: z.string().min(12),
  reason: z.enum(orderCancelReasons).optional(),
})

export const dealerCancelOrderSchema = z.object({
  orderId: z.string().uuid('订单不合法'),
})

export const gameRequestStatusQuerySchema = entryStatusQuerySchema

export const paymentSchema = z.object({
  orderId: z.string().uuid(),
  seatToken: z.string().min(12),
  method: z.enum(playerPaymentMethods).default('wechat'),
})

export const refundOrderSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, '请填写退款原因')
    .max(200, '退款原因最多 200 字'),
})

export const dealerGameRequestSchema = z.object({
  playerId: z.string().uuid(),
  kind: z.enum(entryKinds),
  isHunter: z.boolean().optional(),
  method: z.enum(['cash', 'comp']).default('cash'),
  memberVoucherId: z.string().uuid('票券不合法').nullable().optional(),
})

export const dealerPlayerActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('eliminate'),
    transferToPlayerId: z.string().uuid('接收玩家不合法').nullable().optional(),
    bountyCount: bountyCountSchema.optional(),
  }),
  z.object({
    action: z.literal('leave'),
  }),
  z.object({
    action: z.literal('seat'),
    seatId: z.string().uuid('目标座位不合法'),
  }),
  z.object({
    action: z.literal('move'),
    seatId: z.string().uuid('目标座位不合法'),
  }),
])

export const dealerAddChipsSchema = z.object({
  chips: z.coerce
    .number({ error: '请输入计分数量' })
    .int('计分必须是整数')
    .positive('计分必须大于 0')
    .max(100_000_000, '计分数量过大'),
})

export const dealerScorePointsSchema = z.object({
  points: z.coerce
    .number({ error: '请输入积分' })
    .int('积分必须是整数')
    .positive('积分必须大于 0')
    .max(100_000_000, '积分过大'),
  requestId: z.string().uuid('请求标识不合法'),
})

export const dealerUndoSchema = z
  .object({
    eventId: z.string().uuid('操作不合法').optional(),
    pointRecordId: z.string().uuid('操作不合法').optional(),
  })
  .refine((value) => Boolean(value.eventId) !== Boolean(value.pointRecordId), {
    message: '请选择要撤销的操作',
  })

export const dealerPromotionSchema = z.object({
  isActive: z.boolean(),
})

export const dealerPlayerTransferSchema = z.object({
  playerIds: z
    .array(z.string().uuid('玩家参数不合法'))
    .min(1, '请至少选择一名玩家'),
})

export const dealerChangeSchema = z.object({
  staffId: z.string().uuid('裁判不合法'),
})

export const dealerSessionSchema = z.object({
  seatToken: z.string().trim().min(1, '请提供座位码'),
})

export type DealerPlayerAction = z.infer<typeof dealerPlayerActionSchema>
export type DealerAddChipsInput = z.infer<typeof dealerAddChipsSchema>
export type DealerScorePointsInput = z.infer<typeof dealerScorePointsSchema>
export type DealerUndoInput = z.infer<typeof dealerUndoSchema>
export type DealerPromotionInput = z.infer<typeof dealerPromotionSchema>

const maxRevenueRangeMs = 366 * 24 * 60 * 60 * 1000

export const revenueQuerySchema = z
  .object({
    storeId: z.preprocess(
      (value) => (value === '' || value === null ? undefined : value),
      z.string().uuid('门店参数不合法').optional(),
    ),
    from: z.iso.date('开始日期格式不合法').optional(),
    to: z.iso.date('结束日期格式不合法').optional(),
  })
  .refine(({ from, to }) => !from || !to || from <= to, {
    message: '开始日期不能晚于结束日期',
    path: ['to'],
  })
  .refine(
    ({ from, to }) => {
      if (!from || !to) return true
      return Date.parse(to) - Date.parse(from) <= maxRevenueRangeMs
    },
    {
      message: '查询区间不能超过一年',
      path: ['to'],
    },
  )

export interface ApiErrorResponse {
  error?: string
}

export interface LoginResult {
  redirect: string
}

export interface SwitchableIdentity {
  staffId: string
  tenantId: string
  tenantName: string
  role: StaffRole
  name: string
  label: string
}

export interface SwitchableIdentitiesResult {
  identities: SwitchableIdentity[]
}

export interface SandboxTableOption {
  id: string
  name: string
  storeId: string
  storeName: string
}

export interface SandboxDealerOption {
  id: string
  name: string
  storeId: string | null
}

export interface SandboxTemplateOption {
  id: string
  name: string
}

export interface SandboxOptionsResult {
  tables: SandboxTableOption[]
  dealers: SandboxDealerOption[]
  templates: SandboxTemplateOption[]
  idleVillainCount: number
}

export interface CreateSandboxVillainsResult {
  created: number
}

export interface CreateSandboxGameResult {
  gameId: string
  gameTableId: string
  tableId: string
  villainCount: number
}

export interface SeatAvailableVouchers {
  entry: AvailableVoucherView[]
  rebuy: AvailableVoucherView[]
  hunterEntry: AvailableVoucherView[]
  hunterRebuy: AvailableVoucherView[]
}

export interface IssuedVoucherView {
  id: string
  name: string
}

export interface ChannelRedemptionResult {
  requestKey: string
  isPending: boolean
  isReplay: boolean
  vouchers: IssuedVoucherView[]
  availableVouchersByKind: SeatAvailableVouchers | null
}

export interface SeatJoinResult {
  playerId: string | null
  token: string
  /** 本次入场使用的座位码 */
  seatToken: string
  seatNo: number
  entryKind: EntryKind
  isTransfer: boolean
  /** 这手普通入场可用刚发的新客券 */
  isNewCustomerEntryFree: boolean
  newCustomerMemberVoucherId: string | null
  hasBoughtNormal: boolean
  hasBounty: boolean
  availableVouchers: AvailableVoucherView[]
  availableVouchersByKind: SeatAvailableVouchers
  profile: PlayerProfile
  monthPoints: number
}

export interface ProductSkuView {
  id: string
  name: string
  priceAmount: number
}

export interface GameSkuView {
  entry: ProductSkuView
  rebuy: ProductSkuView
  femaleEntry: ProductSkuView | null
  femaleRebuy: ProductSkuView | null
  hunterEntry: ProductSkuView | null
  hunterRebuy: ProductSkuView | null
  hunterFemaleEntry: ProductSkuView | null
  hunterFemaleRebuy: ProductSkuView | null
}

export interface AvailableVoucherView {
  id: string
  voucherId: string | null
  name: string
  valueAmount: number
  appliedAmount: number
  expiresAt: string | null
}

export interface SeatPlayerSummary {
  nickname: string
  avatarUrl: string | null
}

export interface SeatGameStats {
  averageBuyinHands: number | null
  players: SeatPlayerSummary[]
}

export interface SeatGameInfo {
  gameId: string
  gameTableId: string
  gameTemplateId: string
  templateName: string
  startedAt: string | null
  config: GameConfig
  clock: GameClockView | null
  skus: GameSkuView
  availableVouchers: AvailableVoucherView[]
  /** 当前生效的续场上限：按计分结构的当前买入分段，不限时为 null */
  rebuyLimit: number | null
  isPromotionActive: boolean
  awardSummaries: AwardTierSummary[]
  stats: SeatGameStats
  kind: GameKind
}

export interface SeatElsewhere {
  tableName: string
  seatNo: number
}

export interface SeatInfoResult {
  tableName: string
  seatNo: number
  storeId: string
  tenantId: string
  enabledChannels: ChannelKind[]
  /** 租户有上架且带规格的酒水时，座位页才展示点单 */
  hasGoods: boolean
  /** 租户允许扫桌台码进入点单；前台还要 hasGoods 才跳转 */
  isTableQrShopEntry: boolean
  /** 租户已启用拉卡拉聚合收银台，网页端可以直接跳收银台付款 */
  isLakalaEnabled: boolean
  isOccupied: boolean
  /** 当前会员已在本场离座、等待重新安排座位 */
  isWaiting: boolean
  /** 当前会员已在本场其他座位入座时，指向那一席 */
  seatedElsewhere: SeatElsewhere | null
  game: SeatGameInfo | null
}

export interface PlayerGameRequestStatus {
  playerId: string | null
  playerStatus: GamePlayerStatus | null
  requestId: string | null
  orderId: string | null
  isAwaitingDelivery: boolean
  isAwaitingPayment: boolean
  isPaymentConfirming: boolean
  isUnfulfillable: boolean
  confirmedRequests: number
  pendingRequestCount: number
  rebuyCount: number
  rebuyLimit: number | null
  rebuyRemaining: number | null
  hasBounty: boolean
  /** 他人正在为自己的未付报名单付款时的付款人昵称 */
  proxyPayerNickname: string | null
}

export type SignupBeneficiaryGroup = 'visiting' | 'inGame'

export interface SignupBeneficiaryView {
  memberId: string
  nickname: string
  avatarUrl: string | null
  gender: Gender
  group: SignupBeneficiaryGroup
  playerStatus: GamePlayerStatus | null
  needsEntry: boolean
  rebuyCount: number
  rebuyRemaining: number | null
  hasBounty: boolean
}

export interface SignupBeneficiaryListResult {
  visiting: SignupBeneficiaryView[]
  inGame: SignupBeneficiaryView[]
}

export interface GameSeatView {
  seatId: string
  seatNo: number
  memberId: string | null
  playerId: string | null
  nickname: string | null
  avatarUrl: string | null
  gender: Gender | null
  status: 'empty' | GamePlayerStatus
  chips: number
  bountyCount: number
  transferableBountyCount: number
  bountySteals: number
  hasBounty: boolean
  hasBoughtNormal: boolean
  /** 本场已交分的入场+续场手数，来自报名加码事件 */
  signupCount: number
  unpaidOrderId: string | null
  unpaidPayerNickname: string | null
  unpaidEntryCount: number
  unpaidRebuyCount: number
}

export interface PlayerView {
  playerId: string
  memberId: string
  nickname: string
  avatarUrl: string | null
  seatId: string | null
  seatNo: number | null
  status: GamePlayerStatus
  chips: number
  bountyCount: number
  transferableBountyCount: number
  bountySteals: number
  hasBounty: boolean
  hasBoughtNormal: boolean
  eliminatedAt: string | null
}

export interface PendingGameRequestView {
  requestId: string
  playerId: string
  orderId: string
  orderStatus: OrderStatus
  orderAmount: number
  paymentMethod: PaymentMethod | null
  willEliminate: boolean
  seatNo: number | null
  nickname: string
  kind: EntryKind
  isHunter: boolean
  subtotalAmount: number
  adjustmentAmount: number
  amount: number
  chips: number
  stackMultiplier: number
  promotionName: string | null
  status: GameRequestStatus
  createdAt: string
}

export interface CurrentGameTableDealerView {
  gameTableDealerId: string
  staffId: string
  name: string
  startedAt: string
}

/** 级别提升控制权归属：同场最早开桌的运行桌台及其当前在岗裁判 */
export interface GameClockControllerView {
  gameTableId: string
  tableName: string
  staffId: string | null
  staffName: string | null
}

export interface MemberVoucherBalance {
  voucherId: string
  name: string
  valueAmount: number
  status: ListingStatus
  statusLabel: string
  quantity: number
}

export interface MemberListItem {
  id: string
  memberNo: string
  nickname: string
  phone: string | null
  avatarUrl: string | null
  gender: Gender
  level: MemberLevel
  storeId: string | null
  storeName: string
  ticketCount: number
  points: number
  /** 本租户主要票券的可用张数；其他租户的券不会出现在这个对象里 */
  mainVoucherCounts: Record<string, number>
  status: MemberStatus
  statusLabel: string
  isClaimed: boolean
  canDelete: boolean
  lastActiveAt: string | null
  lastActiveAtLabel: string
  createdAt: string
  createdAtLabel: string
}

export interface MemberImportRow {
  row: number
  memberNo: string | null
  nickname: string
  phone: string | null
  gender: Gender
  level: MemberLevel
  role: MemberRole
  status: MemberStatus
  birthday: string | null
}

export interface MemberImportResult {
  created: number
  skipped: MemberImportSkip[]
}

export interface RankingView {
  playerId: string
  nickname: string
  rank: number
  eliminatedAt: string | null
}

export interface OrderItemView {
  id: string
  kind: OrderItemKind
  productSkuId: string
  name: string
  unitPrice: number
  quantity: number
  amount: number
  adjustmentAmount: number
  payableAmount: number
}

export interface PaymentView {
  id: string
  method: PaymentMethod
  status: PaymentStatus
  amount: number
  paidAt: string | null
}

export interface OrderView {
  id: string
  orderNo: string
  status: OrderStatus
  subtotalAmount: number
  adjustmentAmount: number
  amount: number
  items: OrderItemView[]
  payments: PaymentView[]
  createdAt: string
}

export interface OrderPaymentResult {
  order: OrderView
  payments: PaymentView[]
}

export interface GameRequestOrderResult {
  playerId: string | null
  requestId: string | null
  orderId: string
  orderNo: string
  orderStatus: OrderStatus
  requestStatus: GameRequestStatus | null
  subtotalAmount: number
  adjustmentAmount: number
  amount: number
  isPaymentRequired: boolean
  voucher: AvailableVoucherView | null
  requestIds: string[]
  beneficiaryNickname: string | null
}

export interface GameRequestConfirmationResult {
  requestId: string
  playerId: string
  status: 'confirmed'
  chips: number
}

export interface PlayerDeliveryConfirmationResult {
  playerId: string
  requestIds: string[]
  status: 'confirmed'
  chips: number
  handCount: number
  hasHunter: boolean
}

export interface PlayerActionResult {
  playerId: string
  status: GamePlayerStatus
  seatId: string | null
  eliminatedAt: string | null
}

export interface PlayerTransferResult {
  playerIds: string[]
  isSourceClosed: boolean
}

export interface GameTableDealerChangeResult {
  gameTableId: string
  gameTableDealerId: string
  staffId: string
}

export interface PaymentRequestParams {
  timeStamp: string
  nonceStr: string
  package: string
  signType: 'RSA'
  paySign: string
}

export interface PaymentCreateResult {
  paymentId: string
  status: PaymentStatus
  method: PaymentMethod
  /** 微信 JSAPI 调起参数，只有 `method = wechat` 时有值 */
  requestPayment: PaymentRequestParams | null
  /** 拉卡拉聚合收银台地址，只有 `method = lakala` 时有值，前端跳转后轮询订单状态 */
  cashierUrl: string | null
}

export interface PaymentNotificationResult {
  code: 'SUCCESS'
  message: string
}

export interface DealerOperationView {
  id: string
  eventId: string | null
  pointRecordId: string | null
  kind: GameEventKind | 'motivation' | 'score'
  kindLabel: string
  nickname: string | null
  title: string
  detail: string
  occurredAt: string
  canUndo: boolean
  isReversed: boolean
}

export interface DealerIdleTableState {
  status: 'idle'
  tableId: string
  tableName: string
  storeName: string
  runningGames: DealerRunningGameOption[]
  goodsOrders: TableGoodsOrderView[]
}

export interface DealerTableState {
  gameId: string
  gameTableId: string
  gameTemplateId: string
  gameName: string
  templateName: string
  kind: GameKind
  tableId: string
  tableName: string
  storeName: string
  status: GameTableStatus
  gameStatus: GameStatus
  gameStartedAt: string
  gameTableStartedAt: string
  gamePlayerCount: number
  /** 全场已交分手数，含已出局玩家 */
  gameSignupCount: number
  gameSurvivingPlayerCount: number
  tablePlayerCount: number
  runningGameTableCount: number
  runningGames: DealerRunningGameOption[]
  isPromotionEnabled: boolean
  isPromotionActive: boolean
  /** 节流续场：在座不能现金续场，出局后由玩家自己再报名 */
  isSignupThrottled: boolean
  hunter: HunterGameConfig | null
  promotion: PromotionOffer | null
  skus: GameSkuView
  clock: GameClockView | null
  clockController: GameClockControllerView | null
  canControlClock: boolean
  seats: GameSeatView[]
  players: PlayerView[]
  receivablePlayers: PlayerView[]
  waitingPlayers: WaitingPlayerView[]
  reservedPlayers: ReservedPlayerView[]
  pendingGameRequests: PendingGameRequestView[]
  pulledPlayers: PulledPlayerView[]
  goodsOrders: TableGoodsOrderView[]
  rankings: RankingView[]
  totalChips: number
  averageChips: number
  currentReferee: CurrentGameTableDealerView | null
  pointRecords: MemberPointRecordView[]
  operations: DealerOperationView[]
}

export type DealerTableSnapshot = DealerIdleTableState | DealerTableState

export function isDealerTableIdle(
  state: DealerTableSnapshot,
): state is DealerIdleTableState {
  return state.status === 'idle'
}

export interface WaitingPlayerView extends PlayerView {
  sourceGameTableId: string
  sourceTableName: string
}

export interface ReservedPlayerView {
  memberId: string
  nickname: string
  avatarUrl: string | null
  reservedAt: string
}

export interface DealerRunningGameOption {
  id: string
  name: string
  templateName: string
  gamePlayerCount: number
  runningGameTableCount: number
  status: GameStatus
  dealerStaffId: string | null
}

export interface DealerTemplateOption {
  id: string
  name: string
  kind: GameKind
  entryPrice: number
  rebuyPrice: number
  femaleEntryPrice: number | null
  femaleRebuyPrice: number | null
  entryChips: number
  rebuyChips: number
  promotionEnabled: boolean
  motivationsEnabled: boolean
  motivationIds: string[]
}

export interface DealerStaffOption {
  id: string
  name: string
}

export interface DealerMotivationOption {
  id: string
  name: string
  prizes: MotivationPrize[]
}

export interface DealerTableSetup {
  templates: DealerTemplateOption[]
  referees: DealerStaffOption[]
  motivations: DealerMotivationOption[]
  runningGames: DealerRunningGameOption[]
}

export interface DealerSessionResult {
  token: string
  tableId: string
  tableName: string
  storeId: string
  storeName: string
  role: 'dealer' | 'admin'
  setup: DealerTableSetup
}
